import React, { useEffect, useRef } from "react";
import { reduce } from "lodash";
import { connect } from "react-redux";
import { bindActionCreators, Dispatch } from "redux";
import { Action } from "typescript-fsa";
import { Line } from "react-chartjs-2";
import {
    Chart,
    ChartData,
    ChartDataset,
    ChartEvent,
    ChartOptions,
    LegendElement,
    LegendItem,
    Tick,
    TooltipItem,
    TooltipLabelStyle,
} from "chart.js";
import { FormattedMessage, injectIntl } from "react-intl";

import { makeSalesGet } from "@api/index";
import {
    connectToApi,
    ApiConnectedOwnProps,
    UpdateActionProps,
} from "@api/components/connectToApi";
import { DownloadCsvButton } from "@ui/DownloadCsvButton";
import { compose } from "@util/compose";
import { equalDateAndMoment, formatDateDayMonthYear, formatDateMonthYear } from "@util/index";
import * as actions from "../../actions";
import { getChartLegendOptions, getChartTooltipOptions } from "../../constants";
import { SalesState, SalesGroupBy } from "../../models";
import { messages } from "../../messages";
import {
    MusicIdProps,
    getSalesGroupBy,
    getSalesDataDates,
    getSalesDataSets,
    getSalesTypes,
    getSalesSetForProps,
    getSalesLastLoadedForProps,
    getSettingFrom,
    getSettingTo,
    getSettingType,
    IntlProps,
} from "../../selectors";

interface SalesChartStateProps extends ApiConnectedOwnProps {
    datasets: Array<ChartDataset<"line">>;
    dates: string[];
    types: string[];
    groupBy: SalesGroupBy;
    downloadAction: () => Action<any>;
    settingType: "stream" | "download";
}

interface SalesChartDispatchProps {
    updatedChartLegend(p: [string, boolean]): Action<[string, boolean]>;
}

type SalesChartProps = MusicIdProps & SalesChartStateProps & SalesChartDispatchProps &
    IntlProps & UpdateActionProps;

const SalesChart = ({
    dates,
    datasets,
    intl,
    groupBy,
    downloadAction,
    updatedChartLegend,
    types,
    settingType,
}: SalesChartProps) => {
    // Get a reference to chart chart before any event
    const chart = useRef<Chart<"line">>(null);

    const formatDate = groupBy === "day" ? formatDateDayMonthYear(intl) : formatDateMonthYear(intl);
    const data: ChartData<"line"> = {
        labels: dates,
        datasets,
    };
    const options: ChartOptions<"line"> = {
        scales: {
            y: {
                ticks: {
                    callback: (value: number) => intl.formatNumber(value),
                },
                border: {
                    dash: [4, 3],
                },
            },
            x: {
                type: "time",
                ticks: {
                    autoSkip: true,
                    autoSkipPadding: 16,
                    align: "center",
                    maxRotation: 0,
                    includeBounds: false,
                    callback(value: number, index: number, values: Tick[]) {
                        if (index === values.length - 1) {
                            return null;
                        }
                        return formatDate(value);
                    },
                },
                time: {
                    unit: groupBy === "day" ? "day" : "month",
                },
                border: {
                    dash: [4, 3],
                },
            },
        },
        plugins: {
            legend: {
                ...getChartLegendOptions(),
                position: "bottom",
                onClick: (_event: ChartEvent, legendItem: LegendItem, legend: LegendElement<"line">) => {
                    // Call the default
                    legend.chart.toggleDataVisibility(legendItem.index);
                    legend.chart.update();

                    // And update the chart legend in the store.
                    const index = legendItem.datasetIndex;
                    updatedChartLegend([types[index], legendItem.hidden]);
                },
            },
            tooltip: {
                ...getChartTooltipOptions(),
                callbacks: {
                    title: (items: Array<TooltipItem<"line">>) => {
                        return intl.formatDate(items[0].parsed.x, {
                            year: "numeric",
                            month: "numeric",
                            day: "numeric",
                        });
                    },
                    label: ({ dataset, raw }: TooltipItem<"line">) => {
                        let label = "";
                        if (dataset.label) {
                            label = " " + dataset.label + ": ";
                        }
                        label += intl.formatNumber(Number(raw));
                        return label;
                    },
                    labelColor: ({ dataset }: TooltipItem<"line">) => {
                        return {
                            borderRadius: 4,
                            borderColor: dataset.borderColor as string,
                            backgroundColor: dataset.borderColor as string,
                        } as TooltipLabelStyle;
                    },
                    footer: (items: Array<TooltipItem<"line">>) => {
                        const total = reduce(items,
                            (result: number, item: TooltipItem<"line">) => {
                                result += Number(item.raw);
                                return result;
                            }, 0);
                        return intl.formatMessage(messages.salesTotal) + ": " +
                            intl.formatNumber(total);
                    },
                },
            },
        },
    };
    const title = settingType === "stream" ? messages.dspStreamsChartTitle : messages.dspDownloadsChartTitle;
    return (
        <div className="sales-chart">
            <div className="sales-chart__wrap">
                <h5 className="display-5">
                    <FormattedMessage {...title} />
                </h5>
                <Line ref={chart} data={data} options={options} />
            </div>
            <DownloadCsvButton actionCreator={downloadAction} />
        </div>
    );
};

const mapStateToProps = (state: SalesState, props: SalesChartProps) => {
    const from = getSettingFrom(state);
    const to = getSettingTo(state);
    const actionCreators = [ makeSalesGet(props.albumId, props.trackId, props.artistId, from, to) ];
    const downloadAction = makeSalesGet(props.albumId, props.trackId, props.artistId, from, to, true);
    const requestId = [ from.format() + "_" + to.format() ];
    const set = getSalesSetForProps(state, props);
    const dates = getSalesDataDates(state, props);
    let lastLoaded = getSalesLastLoadedForProps(state, props);
    // If the settings dates are different to the loaded set, load again
    if (lastLoaded != null && set != null &&
        (!equalDateAndMoment(set.from, from) || !equalDateAndMoment(set.to, to))
    ) {
        lastLoaded = null;
    }
    return {
        datasets: getSalesDataSets(state, props),
        dates: dates == null ? [] : dates,
        types: getSalesTypes(state, props),
        groupBy: getSalesGroupBy(state, props),
        settingType: getSettingType(state),
        actionCreators,
        requestId,
        lastLoaded: [ lastLoaded ],
        downloadAction,
    } as SalesChartStateProps;
};

const mapDispatchToProps = (dispatch: Dispatch<SalesState>): SalesChartDispatchProps => {
    return {
        updatedChartLegend: bindActionCreators(actions.updatedChartLegend, dispatch),
    };
};

const ConnectedSalesChart = compose<SalesChartProps, MusicIdProps>(
    injectIntl,
    connect(mapStateToProps, mapDispatchToProps),
    connectToApi({}),
)(SalesChart);

export {
    SalesChart as SalesChartPresenter,
    ConnectedSalesChart as SalesChart,
    SalesChartProps,
};
